// ChoiceButton — a titled option inside a dialog or panel ("Ransom", "Hold"…).
// One look for every pick-one list: title, a line of consequence beneath, and
// a selected state. Native <button>, so keyboard and screen readers just work.

import type * as React from 'react';
import { cn } from '../../lib/utils.js';

export interface ChoiceButtonProps extends Omit<React.ComponentProps<'button'>, 'title'> {
  readonly title: React.ReactNode;
  readonly description?: React.ReactNode;
  readonly selected?: boolean;
}

function ChoiceButton({
  title,
  description,
  selected = false,
  className,
  type = 'button',
  ...props
}: ChoiceButtonProps) {
  return (
    <button
      type={type}
      data-slot="choice-button"
      aria-pressed={selected}
      className={cn(
        'flex w-full flex-col gap-0.5 rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        selected
          ? 'border-primary/70 bg-primary/10'
          : 'border-border/50 bg-card/60 hover:bg-muted/50',
        className,
      )}
      {...props}
    >
      <span className="text-sm font-medium text-foreground">{title}</span>
      {description && (
        <span className="text-[11px] leading-tight text-muted-foreground">{description}</span>
      )}
    </button>
  );
}

export { ChoiceButton };
